import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { logError } from 'utils/logger';
import { EquipmentDelivery } from '../types';
import { equipmentDeliverySchema } from '../model';
import { useNotifications } from '../../notification';
import { UPDATE_EQUIPMENT, GET_DELIVERY_ADDRESS } from '../api/equipment';
import { DeliveryAddressResponse, DeliveryAddressVariables } from '../api/equipment/types';
import { removeGraphQLMEtaFields } from '../../../utils/removeGraphQLMEtaFields';

export function useEquipmentDelivery(
  id?: string | undefined,
  onReceivedDeliveryAddress?: (address: EquipmentDelivery) => void
) {
  const { showNotification } = useNotifications();

  const { data, loading } = useQuery<DeliveryAddressResponse, DeliveryAddressVariables>(GET_DELIVERY_ADDRESS, {
    skip: !id,
    variables: { id: id! },
    onCompleted({ equipment }) {
      if (equipment?.deliveryAddress && typeof onReceivedDeliveryAddress === 'function') {
        onReceivedDeliveryAddress(removeGraphQLMEtaFields(equipment.deliveryAddress));
      }
    },
  });

  const [onFetchDeliveryAddress] = useLazyQuery<DeliveryAddressResponse, DeliveryAddressVariables>(
    GET_DELIVERY_ADDRESS,
    {
      variables: { id: id! },
    }
  );

  const [onUpdateDeliveryAddress] = useMutation(UPDATE_EQUIPMENT);

  const handleUpdateDeliveryAddress = async (deliveryData: EquipmentDelivery) => {
    try {
      const { data, error } = equipmentDeliverySchema.safeParse(deliveryData);

      if (error) return;

      await onUpdateDeliveryAddress({ variables: { input: { equipmentId: id, ...data } } });
      showNotification('Delivery address updated successfully.');
    } catch (error) {
      logError(error);
      showNotification('Error updating delivery address', { type: 'error' });
    }
  };

  const handleFetchDeliveryAddress = async (id: string) => {
    try {
      const { data } = await onFetchDeliveryAddress({ variables: { id } });

      return data?.equipment?.deliveryAddress ?? null;
    } catch (error) {
      logError(error);
      return null;
    }
  };

  return {
    isLoading: loading,
    handleFetchDeliveryAddress,
    handleUpdateDeliveryAddress,
    deliveryAddress: data?.equipment?.deliveryAddress,
  };
}
